import { useEffect, useState } from "react";

/**
 * Google Maps JS loader for the panel. The browser key is deployment config,
 * fetched once from the auth-free /web-config endpoint (never baked into the
 * bundle, so one build serves every installation). A missing key — or an
 * unreachable Google — resolves to null and every map feature degrades
 * gracefully to the plain form inputs.
 */

declare global {
  interface Window {
    __idppMapsReady?: (() => void) | undefined;
  }
}

let loadPromise: Promise<typeof google.maps | null> | null = null;

async function load(): Promise<typeof google.maps | null> {
  let key: string | null = null;
  try {
    const res = await fetch("/web-config");
    if (res.ok) {
      key = ((await res.json()) as { googleMapsBrowserKey: string | null }).googleMapsBrowserKey;
    }
  } catch {
    /* API unreachable — behave exactly like "maps disabled" */
  }
  if (!key) return null;
  try {
    // callback= (not script onload): with loading=async the script bootstraps
    // lazily, and only the callback guarantees google.maps is actually usable.
    await new Promise<void>((resolve, reject) => {
      window.__idppMapsReady = resolve;
      const s = document.createElement("script");
      s.src =
        "https://maps.googleapis.com/maps/api/js?key=" +
        encodeURIComponent(key) +
        "&v=weekly&loading=async&callback=__idppMapsReady";
      s.async = true;
      s.onerror = () => reject(new Error("Google Maps script failed to load"));
      document.head.appendChild(s);
    });
    return google.maps;
  } catch {
    return null;
  }
}

/** Resolves the maps namespace once per session; null = disabled/unreachable. */
export function loadGoogleMaps(): Promise<typeof google.maps | null> {
  loadPromise ??= load();
  return loadPromise;
}

/** undefined while loading, null when maps are disabled/unreachable. */
export function useGoogleMaps(): typeof google.maps | null | undefined {
  const [maps, setMaps] = useState<typeof google.maps | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void loadGoogleMaps().then((m) => {
      if (alive) setMaps(m);
    });
    return () => {
      alive = false;
    };
  }, []);
  return maps;
}
